/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {catchError, Observable, of} from 'rxjs';
import {Uuid} from '../../components/util/uuid';

import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface SiteModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface SiteLookupModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface SiteCreativeSummaryItem {
  creative?: {id?: string; name?: string};
  lineItem?: {id?: string; name?: string};
  hash?: string;
  status?: string;
  scanInfo?: Array<{
    scanTime?: string;
    policyViolations?: any[];
  }>;
}

export type SiteCreativeSummaryType = 'BLOCKED' | 'INITIALLY_BLOCKED' | 'ALLOWED';

export interface SiteCreativeSummaryParams {
  summaryType: SiteCreativeSummaryType;
  page: number;
  pageSize: number;
  locale?: string;
}

export interface SiteArticle {
  keywords?: string[];
  keywordsRead?: string;
  [key: string]: any;
}

export interface SiteCountryPage {
  countries: Array<{country?: string; count?: number}>;
}

@Injectable({providedIn: 'root'})
export class SiteApiService extends SignalAdminResourceApiBase<SiteModel> {
  constructor(client: AdnApiClientService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'sites', {}, () => localUserProfile?.isReadOnly() || false);
  }

  create(defaults: Partial<SiteModel> = {}): SiteModel {
    return {id: Uuid.generate(), ...defaults} as SiteModel;
  }

  listEarningsAccounts(): Observable<SignalAdminResourcePage<SiteLookupModel>> {
    return this.listLookup('earningsaccounts', true);
  }

  listSiteGroups(): Observable<SignalAdminResourcePage<SiteLookupModel>> {
    return this.listLookup('sitegroups', true);
  }

  listBlocklists(): Observable<SignalAdminResourcePage<SiteLookupModel>> {
    return this.listLookup('blocklists', true);
  }

  listRulesets(): Observable<SignalAdminResourcePage<SiteLookupModel>> {
    return this.listLookup('rulesets', false);
  }

  listTeams(): Observable<SignalAdminResourcePage<SiteLookupModel>> {
    return this.listLookup('teams', true);
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<SiteModel>> {
    return this.client.request<SignalAdminResourcePage<SiteModel>>({
      method: 'GET',
      url: this.resourceUri('sites'),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        pageSize: 1001,
        ...params
      }
    });
  }

  getMany(ids: string[]): Observable<SignalAdminResourcePage<SiteModel>> {
    if (!ids.length) {
      return of({results: [], totalCount: 0, page: 0});
    }
    return this.listOptions({
      id: ids.join(';'),
      pageSize: ids.length,
      includeActive: true,
      includeInactive: true,
      includeHidden: true
    });
  }

  bulkSave(models: Partial<SiteModel>[]): Observable<void> {
    return this.saveMany(models);
  }

  getAvailableCountries(): Observable<SiteCountryPage> {
    return this.client.request<SiteCountryPage>({
      method: 'GET',
      url: this.resourceUri(['sites', 'availablecountries']),
      params: {}
    });
  }

  getCreativeSummary(
    siteId: string,
    params: SiteCreativeSummaryParams
  ): Observable<SignalAdminResourcePage<SiteCreativeSummaryItem>> {
    return this.client.request<SignalAdminResourcePage<SiteCreativeSummaryItem>>({
      method: 'GET',
      url: this.resourceUri(['sites', siteId, 'adncreativesummary']),
      params: {
        orderBy: 'name',
        concise: true,
        orderByDirection: 'ASCENDING',
        includeActive: false,
        includeInactive: false,
        includeHidden: false,
        joins: '',
        where: '',
        ...params
      }
    });
  }

  getContentProfile(siteId: string, url: string): Observable<any> {
    return this.client.request<any>({
      method: 'GET',
      url: this.resourceUri('contentsummary'),
      params: {format: 'json', concise: true, site: siteId, url}
    }).pipe(catchError((response) => of(response?.data)));
  }

  getAdCount(siteId: string): Observable<any> {
    return this.getContentSummaryData('adcount', siteId);
  }

  getAdCounts(siteId: string): Observable<any> {
    return this.getContentSummaryData('adcounts', siteId);
  }

  listRecentArticles(siteId: string, domain: string): Observable<SignalAdminResourcePage<SiteArticle>> {
    return this.listArticles('recentarticles', {
      domain,
      site: siteId,
      maxAgeInHours: 24,
      objectState: 'ACTIVE'
    });
  }

  listViewedArticles(siteId: string, domain: string): Observable<SignalAdminResourcePage<SiteArticle>> {
    return this.listArticles('viewedarticles', {
      domain,
      site: siteId,
      objectState: 'ACTIVE'
    });
  }

  refreshContentProfile(url: string): Observable<void> {
    return this.client.request<void>({
      method: 'GET',
      url: this.resourceUri(['crawler', 'refresh']),
      params: {url}
    });
  }

  getConsentStatistics(siteId: string): Observable<any> {
    return this.client.request<any>({
      method: 'GET',
      url: this.resourceUri('consentstatistic'),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        pageSize: 1001,
        site: siteId
      }
    });
  }

  private listLookup(path: string, minimal: boolean): Observable<SignalAdminResourcePage<SiteLookupModel>> {
    return this.client.request<SignalAdminResourcePage<SiteLookupModel>>({
      method: 'GET',
      url: this.resourceUri(path),
      params: {
        includeActive: true,
        includeInactive: false,
        includeHidden: false,
        orderBy: 'name',
        pageSize: 1001,
        minimal
      }
    });
  }

  private getContentSummaryData(path: string, siteId: string): Observable<any> {
    return this.client.request<any>({
      method: 'GET',
      url: this.resourceUri(['contentsummary', path]),
      params: {site: siteId}
    }).pipe(catchError((response) => of(response?.data)));
  }

  private listArticles(
    path: string,
    params: Record<string, any>
  ): Observable<SignalAdminResourcePage<SiteArticle>> {
    return this.client.request<SignalAdminResourcePage<SiteArticle>>({
      method: 'GET',
      url: this.resourceUri(['targetingquery', path]),
      params
    });
  }
}
